import moment from 'moment';
import type { DentalOfficeShiftCardStatus } from '@tempmee/react-blocks';
import { SHIFT_CARD_STATUS, type TShiftCardData } from '~/types/ShiftCard';
import { StatusEnum } from '~/utils/Enum';
import { computeHours, formatRate, parseCardDate, resolveProfession } from '~/utils/shiftCard';
import { getCalendarJobStatus } from '../../utils';

/**
 * Minimal shape of a schedule job (redux `state.job.jobList`, raw API) — only the
 * fields the shift card consumes. The raw job uses `is_canceled` / `offer_final_reciever`
 * (note the API spellings), so those are matched here rather than the `IShift` model.
 */
export interface IScheduleJobReceiver {
  first_name?: string;
  last_name?: string;
  profile_image?: string;
}

export interface IScheduleJobCounter {
  isFavorite?: boolean;
}

export interface IScheduleJob {
  id?: string | number;
  status?: string;
  is_canceled?: boolean;
  local_date?: string;
  local_start_time?: string;
  local_end_time?: string;
  rate?: number;
  professionName?: string;
  subProfession?: string | null;
  isOnsiteInterview?: boolean;
  /** Absolute shift start/end — drives the in-progress ("current") detection. */
  job_datetime?: string | Date;
  job_datetime_end?: string | Date;
  offer_final_reciever?: IScheduleJobReceiver | null;
  offer_owner?: { office_name?: string; office?: string } | null;
  multipleCounters?: IScheduleJobCounter[] | null;
  favoriteInvites?: unknown[] | null;
}

/** Maps the legacy `StatusEnum` label (from `getCalendarJobStatus`) to a block status. */
const STATUS_LABEL_TO_CARD_STATUS: Record<string, DentalOfficeShiftCardStatus> = {
  [StatusEnum.open]: SHIFT_CARD_STATUS.OPEN,
  [StatusEnum.scheduled]: SHIFT_CARD_STATUS.SCHEDULED,
  [StatusEnum.today]: SHIFT_CARD_STATUS.CURRENT,
  [StatusEnum.pending]: SHIFT_CARD_STATUS.CONFIRM_CANDIDATES,
  [StatusEnum.completed]: SHIFT_CARD_STATUS.COMPLETED,
  [StatusEnum.cancelled]: SHIFT_CARD_STATUS.CANCELLED,
  [StatusEnum.expired]: SHIFT_CARD_STATUS.CANCELLED,
  [StatusEnum.cancelledOrExpired]: SHIFT_CARD_STATUS.CANCELLED,
};

/**
 * Labels the block collapses into `cancelled` but that we still want to distinguish
 * on the card via the `statusLabel` override.
 */
const STATUS_LABEL_OVERRIDE: Record<string, string> = {
  [StatusEnum.expired]: 'Expired',
  [StatusEnum.cancelledOrExpired]: 'Cancelled or expired',
};

/**
 * A shift is "in progress" when now falls within its [start, end) window. Mirrors the
 * Today's Professionals card (GOTU-5195) so both DS surfaces agree on what "current" means.
 */
const isShiftInProgress = (start?: string | Date, end?: string | Date): boolean => {
  if (!start || !end) {
    return false;
  }
  const now = moment();
  return now.isSameOrAfter(moment(start)) && now.isBefore(moment(end));
};

/**
 * Translates a raw schedule job into the props for the DS `DentalOfficeShiftCard`.
 * Pure and presentational — reuses `getCalendarJobStatus` so the today/cancelled
 * decision stays single-sourced in `Schedule/utils`.
 */
export const mapScheduleJobToShiftCard = (job: IScheduleJob): TShiftCardData => {
  const { label } = getCalendarJobStatus(job);
  let status: DentalOfficeShiftCardStatus =
    (label ? STATUS_LABEL_TO_CARD_STATUS[label] : undefined) ?? SHIFT_CARD_STATUS.SCHEDULED;
  const statusLabel = label ? STATUS_LABEL_OVERRIDE[label] : undefined;

  // Once a shift is underway the backend advances its status to `pay-out`, which
  // getCalendarJobStatus surfaces as `completed`. Treat an in-progress shift with an
  // assigned professional as the "current" shift instead, so the office can still report a
  // no-show / walk-out from the Schedule List — matching the Today's Professionals card.
  // The 30-minute report window itself is enforced in useCurrentShiftCardMenu.
  if (
    status !== SHIFT_CARD_STATUS.CURRENT &&
    !job.is_canceled &&
    !job.isOnsiteInterview &&
    job.offer_final_reciever &&
    isShiftInProgress(job.job_datetime, job.job_datetime_end)
  ) {
    status = SHIFT_CARD_STATUS.CURRENT;
  }

  const data: TShiftCardData = {
    status,
    date: parseCardDate(job.local_date),
    startTime: job.local_start_time ?? '',
    endTime: job.local_end_time,
    hours: computeHours(job.local_start_time, job.local_end_time),
    rate: formatRate(job.rate),
    profession: resolveProfession(job.professionName, job.subProfession),
    office: job.offer_owner?.office_name ?? job.offer_owner?.office ?? '',
    interview: Boolean(job.isOnsiteInterview),
  };

  if (statusLabel) {
    data.statusLabel = statusLabel;
  }

  if (status === SHIFT_CARD_STATUS.CONFIRM_CANDIDATES) {
    data.candidates = (job.multipleCounters ?? []).map((counter) => ({
      isFavorite: Boolean(counter?.isFavorite),
    }));
  } else if (status === SHIFT_CARD_STATUS.OPEN) {
    data.invitedCount = job.favoriteInvites?.length ?? 0;
  } else {
    const receiver = job.offer_final_reciever;
    const name = `${receiver?.first_name ?? ''} ${receiver?.last_name ?? ''}`.trim();
    if (name || receiver?.profile_image) {
      data.person = {
        ...(name ? { name } : {}),
        ...(receiver?.profile_image ? { avatarUrl: receiver.profile_image } : {}),
      };
    }
  }

  return data;
};
